import { useState } from 'react';

import { Collapse } from '@components/animators/collapse';
import { FileFieldType } from '@components/form/v2';
import { getNumberRestrictionError, NumberField } from '@components/number-field';
import { Stack } from '@components/stack';
import { LabeledSwitch } from '@components/switch';
import { getScopedTranslate } from '@locale/translate';
import { getMaxFileSizeMb } from '@utils/file-size';

import { useFormBuilderContext } from '../context';

export const MaxFileSizeSetting = ({ field }: { field: FileFieldType }) => {
  const {
    store: { actions },
  } = useFormBuilderContext();
  const [inputValue, setInputValue] = useState<number | undefined>(
    field.maxFileSizeMb ?? undefined,
  );
  const limit = getMaxFileSizeMb({ restrictFileSize: true, maxFileSizeMb: field.maxFileSizeMb });
  const error = getNumberRestrictionError(inputValue, {
    min: 1,
    disableDecimal: true,
    disableNegative: true,
  });
  const setMaxFileSizeMb = actions.createChangeHandler(field, 'maxFileSizeMb');
  const handleChange = (value: number | undefined) => {
    setInputValue(value);
    if (value === undefined) {
      setMaxFileSizeMb(null);
      return;
    }
    if (getMaxFileSizeMb({ restrictFileSize: true, maxFileSizeMb: value }) !== undefined) {
      setMaxFileSizeMb(value);
    }
  };

  return (
    <Stack space={4}>
      <LabeledSwitch
        label={t('maxFileSize')}
        value={Boolean(field.restrictFileSize)}
        onText={limit === undefined ? t('noLimit') : t('megabytes', { size: limit })}
        onChange={actions.createChangeHandler(field, 'restrictFileSize')}
      />
      <Collapse isCollapsed={!field.restrictFileSize}>
        <NumberField
          label={t('maxFileSize')}
          hideLabel
          isDecimal
          placeholder={t('placeholder')}
          defaultValue={field.maxFileSizeMb ?? undefined}
          onChange={handleChange}
          error={error}
          dataTestid="max-file-size-input"
        />
      </Collapse>
    </Stack>
  );
};

const { t } = getScopedTranslate({
  en: {
    maxFileSize: 'Max file size',
    noLimit: 'No limit',
    megabytes: '{size} MB',
    placeholder: 'Size in MB',
  },
});
